import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle, Loader2, Inbox, Search, FileText,
  User, Calendar, Ruler, Tag, Eye, EyeOff, ImageIcon,
  XCircle, DollarSign, LayoutTemplate,
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import AdminLayout from './AdminLayout';
import PageThumbnailPreview from '../../components/Editor/PageThumbnailPreview';

const ApprovalThumbnail = ({ item, className = '', fit = 'cover' }) => {
  const [imgError, setImgError] = useState(false);
  const stored = item.thumbnail || item.preview_url;
  const firstPage = item.template_json?.pages?.[0];
  const cw = item.canvas_width  || item.canvas_size?.width  || item.template_json?.canvas_width  || 1080;
  const ch = item.canvas_height || item.canvas_size?.height || item.template_json?.canvas_height || 1080;

  if (stored && !imgError) {
    return (
      <img
        src={stored}
        alt="thumb"
        className={`w-full h-full ${fit === 'contain' ? 'object-contain' : 'object-cover'} ${className}`}
        onError={() => setImgError(true)}
      />
    );
  }
  if (firstPage) {
    return <PageThumbnailPreview page={firstPage} canvasSize={{ width: cw, height: ch }} fillContainer />;
  }
  return <ImageIcon className="w-6 h-6 text-gray-200" />;
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ── Published tab (existing behaviour) ────────────────────────────────────────

const PublishedTab = () => {
  const { toast } = useToast();
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [unpublishingId, setUnpublishingId] = useState(null);
  const [previewItem, setPreviewItem] = useState(null);

  const token = () => localStorage.getItem('admin_token');

  const handleAuth = (res) => {
    if (res.status === 401) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      window.location.href = '/admin/login';
      return false;
    }
    return true;
  };

  const fetchApprovals = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/content/submissions?status_filter=approved`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!handleAuth(res)) return;
      if (!res.ok) {
        toast({ title: 'Error', description: `Failed to load approvals (${res.status})`, variant: 'destructive' });
        setApprovals([]);
        return;
      }
      setApprovals((await res.json()) || []);
    } catch {
      toast({ title: 'Error', description: 'Failed to load approvals', variant: 'destructive' });
      setApprovals([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchApprovals(); }, []);

  const handleUnpublish = async (item) => {
    setUnpublishingId(item.id);
    try {
      const res = await fetch(`${API_URL}/api/admin/content/submissions/${item.id}/unpublish`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!handleAuth(res)) return;
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        toast({ title: 'Failed', description: d.detail || 'Could not unpublish', variant: 'destructive' });
        return;
      }
      toast({ title: 'Unpublished', description: `"${item.name || item.title}" returned to pending review.` });
      setApprovals(prev => prev.filter(a => a.id !== item.id));
      if (previewItem?.id === item.id) setPreviewItem(null);
    } catch {
      toast({ title: 'Error', description: 'Could not unpublish template', variant: 'destructive' });
    } finally {
      setUnpublishingId(null);
    }
  };

  const fmt = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return iso; }
  };

  const filtered = approvals.filter(s => {
    const q = searchQuery.toLowerCase();
    return !q
      || (s.name || s.title || '').toLowerCase().includes(q)
      || (s.submitted_by_name || s.designer_name || '').toLowerCase().includes(q);
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="relative w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            placeholder="Search by title or designer..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-9 pr-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400 w-full"
          />
        </div>
        <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl px-4 py-2 flex-shrink-0">
          <CheckCircle className="w-4 h-4" />
          <span className="text-sm font-medium">{approvals.length} published</span>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-gray-100 rounded-2xl flex flex-col items-center justify-center py-16">
          <Inbox className="w-14 h-14 text-gray-200 mb-4" />
          <p className="font-medium text-gray-700">
            {approvals.length === 0 ? 'No approved templates yet' : 'No results match your search'}
          </p>
          <p className="text-sm text-gray-400 mt-1">
            {approvals.length === 0 ? 'Approved submissions will appear here.' : 'Try a different search term.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(s => {
            const isUnpublishing = unpublishingId === s.id;
            return (
              <div key={s.id} className="bg-white border border-emerald-100 rounded-2xl p-4 flex items-center gap-4 hover:shadow-sm transition-all">
                <div className="relative w-14 h-14 rounded-xl overflow-hidden bg-gray-50 border border-gray-100 flex-shrink-0 flex items-center justify-center">
                  <ApprovalThumbnail item={s} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-gray-900 text-sm truncate">{s.name || s.title || 'Untitled'}</p>
                    {s.template_type && (
                      <span className="text-[11px] px-2 py-0.5 rounded-md border border-gray-200 text-gray-500 capitalize">
                        {s.template_type.replace(/_/g, ' ')}
                      </span>
                    )}
                    {s.is_premium && (
                      <span className="text-[11px] px-2 py-0.5 rounded-md bg-amber-100 text-amber-700 font-medium">Premium</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-400 flex-wrap">
                    <span className="flex items-center gap-1"><User className="w-3 h-3" />{s.submitted_by_name || s.designer_name || 'Unknown'}</span>
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />Approved {fmt(s.reviewed_at || s.submitted_at)}</span>
                    {(s.canvas_width || s.canvas_size?.width) && (
                      <span className="flex items-center gap-1"><Ruler className="w-3 h-3" />{s.canvas_width || s.canvas_size?.width}×{s.canvas_height || s.canvas_size?.height}px</span>
                    )}
                    {s.tags?.length > 0 && (
                      <span className="flex items-center gap-1"><Tag className="w-3 h-3" />{s.tags.slice(0, 3).join(', ')}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] font-medium bg-emerald-100 text-emerald-700 flex-shrink-0">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  Approved
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => setPreviewItem(previewItem?.id === s.id ? null : s)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-700 transition-colors"
                  >
                    <Eye className="w-3.5 h-3.5" /> Preview
                  </button>
                  <button
                    onClick={() => handleUnpublish(s)}
                    disabled={isUnpublishing}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-red-500 hover:bg-red-600 disabled:opacity-40 text-white rounded-lg transition-colors"
                  >
                    {isUnpublishing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <EyeOff className="w-3.5 h-3.5" />}
                    Unpublish
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {previewItem && (
        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <FileText className="w-5 h-5 text-violet-500" />
              <div>
                <p className="font-semibold text-gray-900 text-sm">{previewItem.name || previewItem.title || 'Untitled'}</p>
                <p className="text-xs text-gray-400">by {previewItem.submitted_by_name || previewItem.designer_name || 'Unknown'}</p>
              </div>
            </div>
            <button onClick={() => setPreviewItem(null)} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 text-lg font-light">×</button>
          </div>
          {(() => {
            const cw = previewItem.canvas_width || previewItem.template_json?.canvas_width || 1080;
            const ch = previewItem.canvas_height || previewItem.template_json?.canvas_height || 1080;
            return (
              <div className="flex justify-center">
                <div className="rounded-xl border border-gray-100 bg-gray-50 overflow-hidden relative" style={{ width: 'min(28rem, 100%)', aspectRatio: `${cw} / ${ch}`, maxHeight: '24rem' }}>
                  <ApprovalThumbnail item={previewItem} fit="contain" />
                </div>
              </div>
            );
          })()}
          {previewItem.description && <p className="mt-4 text-sm text-gray-500 leading-relaxed">{previewItem.description}</p>}
        </div>
      )}
    </div>
  );
};

// ── Creator Hub tab ────────────────────────────────────────────────────────────

const STATUS_COLORS = {
  pending:  'bg-amber-50 border-amber-100 text-amber-700',
  approved: 'bg-blue-50 border-blue-100 text-blue-700',
  published: 'bg-emerald-50 border-emerald-100 text-emerald-700',
  rejected: 'bg-red-50 border-red-100 text-red-600',
};

const CreatorHubTab = () => {
  const { toast } = useToast();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [actionId, setActionId] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [previewItem, setPreviewItem] = useState(null);

  const token = () => localStorage.getItem('admin_token');

  const fetch_ = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status: statusFilter, source: 'creator', limit: 100 });
      const res = await fetch(`${API_URL}/api/admin/content/templates?${params}`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (res.status === 401) { window.location.href = '/admin/login'; return; }
      if (!res.ok) { toast({ title: 'Error', description: `Failed to load (${res.status})`, variant: 'destructive' }); return; }
      const data = await res.json();
      setTemplates(Array.isArray(data) ? data : []);
    } catch {
      toast({ title: 'Error', description: 'Network error', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const doAction = async (id, method, path, body, successMsg, onSuccess) => {
    setActionId(id);
    try {
      const res = await fetch(`${API_URL}/api/admin/content/templates/${id}/${path}`, {
        method,
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        ...(body ? { body: JSON.stringify(body) } : {}),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        toast({ title: 'Failed', description: d.detail || `Error ${res.status}`, variant: 'destructive' });
        return;
      }
      toast({ title: 'Done', description: successMsg });
      onSuccess?.();
      await fetch_();
    } catch {
      toast({ title: 'Error', description: 'Request failed', variant: 'destructive' });
    } finally {
      setActionId(null);
    }
  };

  const handleApprove = (t) => doAction(t.id, 'PATCH', 'approve', null, `"${t.name}" approved.`);
  const handlePublish = (t) => doAction(t.id, 'PATCH', 'publish', null, `"${t.name}" published to marketplace.`);
  const handleRejectConfirm = () => {
    if (!rejectTarget) return;
    doAction(rejectTarget.id, 'PATCH', 'reject', { status: 'rejected', rejection_reason: rejectReason.trim() || null }, `"${rejectTarget.name}" rejected.`, () => {
      setRejectTarget(null);
      setRejectReason('');
    });
  };

  const fmt = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return iso; }
  };

  const filtered = templates.filter(t => {
    const q = searchQuery.toLowerCase();
    return !q || (t.name || '').toLowerCase().includes(q);
  });

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            placeholder="Search templates…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-9 pr-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500/20 w-52"
          />
        </div>
        <div className="flex gap-1.5">
          {['pending', 'approved', 'published', 'rejected'].map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition-colors ${statusFilter === s ? 'bg-violet-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}
            >
              {s}
            </button>
          ))}
        </div>
        <span className="ml-auto text-xs text-gray-400">{filtered.length} template{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-gray-100 rounded-2xl flex flex-col items-center justify-center py-16">
          <Inbox className="w-14 h-14 text-gray-200 mb-4" />
          <p className="font-medium text-gray-700">No {statusFilter} creator templates</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(t => {
            const busy = actionId === t.id;
            const isPending = t.status === 'pending';
            const isApproved = t.status === 'approved';
            return (
              <div key={t.id} className={`bg-white rounded-2xl p-4 flex items-center gap-4 hover:shadow-sm transition-all border ${STATUS_COLORS[t.status] || 'border-gray-100'}`}>
                <div className="w-14 h-14 rounded-xl overflow-hidden bg-gray-50 border border-gray-100 flex-shrink-0 flex items-center justify-center">
                  <ApprovalThumbnail item={t} />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-gray-900 text-sm truncate">{t.name || 'Untitled'}</p>
                    {t.licensing_type === 'one_time' && (
                      <span className="flex items-center gap-0.5 text-[11px] px-2 py-0.5 rounded-md bg-violet-100 text-violet-700 font-medium">
                        <DollarSign className="w-3 h-3" />{((t.price_cents || 0) / 100).toFixed(2)}
                      </span>
                    )}
                    {t.licensing_type === 'free' && (
                      <span className="text-[11px] px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-700 font-medium">Free</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-400 flex-wrap">
                    {t.submitted_at && (
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />Submitted {fmt(t.submitted_at)}</span>
                    )}
                    {t.canvas_width && (
                      <span className="flex items-center gap-1"><Ruler className="w-3 h-3" />{t.canvas_width}×{t.canvas_height}px</span>
                    )}
                    {t.tags?.length > 0 && (
                      <span className="flex items-center gap-1"><Tag className="w-3 h-3" />{t.tags.slice(0, 3).join(', ')}</span>
                    )}
                    {t.description && (
                      <span className="text-gray-400 truncate max-w-xs">{t.description}</span>
                    )}
                  </div>
                  {t.rejection_reason && (
                    <p className="mt-1 text-xs text-red-500 truncate">Reason: {t.rejection_reason}</p>
                  )}
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => setPreviewItem(previewItem?.id === t.id ? null : t)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-700 transition-colors"
                  >
                    <Eye className="w-3.5 h-3.5" /> Preview
                  </button>

                  {isPending && (
                    <>
                      <button
                        onClick={() => handleApprove(t)}
                        disabled={busy}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded-lg transition-colors"
                      >
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />}
                        Approve
                      </button>
                      <button
                        onClick={() => { setRejectTarget(t); setRejectReason(''); }}
                        disabled={busy}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-red-500 hover:bg-red-600 disabled:opacity-40 text-white rounded-lg transition-colors"
                      >
                        <XCircle className="w-3.5 h-3.5" /> Reject
                      </button>
                    </>
                  )}

                  {isApproved && (
                    <button
                      onClick={() => handlePublish(t)}
                      disabled={busy}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white rounded-lg transition-colors"
                    >
                      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LayoutTemplate className="w-3.5 h-3.5" />}
                      Publish
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {previewItem && (
        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <FileText className="w-5 h-5 text-violet-500" />
              <div>
                <p className="font-semibold text-gray-900 text-sm">{previewItem.name || 'Untitled'}</p>
                <p className="text-xs text-gray-400">{previewItem.description || 'No description'}</p>
              </div>
            </div>
            <button onClick={() => setPreviewItem(null)} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 text-lg font-light">×</button>
          </div>
          {(() => {
            const cw = previewItem.canvas_width || previewItem.template_json?.canvas_width || 1080;
            const ch = previewItem.canvas_height || previewItem.template_json?.canvas_height || 1080;
            return (
              <div className="flex justify-center">
                <div className="rounded-xl border border-gray-100 bg-gray-50 overflow-hidden relative" style={{ width: 'min(28rem, 100%)', aspectRatio: `${cw} / ${ch}`, maxHeight: '24rem' }}>
                  <ApprovalThumbnail item={previewItem} fit="contain" />
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Reject modal */}
      {rejectTarget && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md">
            <h3 className="font-bold text-gray-900 mb-1">Reject template</h3>
            <p className="text-sm text-gray-500 mb-4">Optionally explain why so the creator can fix and resubmit.</p>
            <textarea
              rows={3}
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder="Rejection reason (optional)"
              className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-red-400 mb-4"
            />
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setRejectTarget(null)}
                className="px-4 py-2 text-sm border border-gray-200 rounded-xl hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRejectConfirm}
                disabled={!!actionId}
                className="flex items-center gap-1.5 px-4 py-2 text-sm bg-red-500 hover:bg-red-600 text-white rounded-xl font-semibold disabled:opacity-50"
              >
                {actionId ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Page ───────────────────────────────────────────────────────────────────────

const AdminApprovals = () => {
  const [tab, setTab] = useState('creator');

  const tabs = [
    { key: 'creator', label: 'Creator Hub' },
    { key: 'published', label: 'Published (Designer Portal)' },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6 w-full">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Approvals</h1>
          <p className="text-sm text-gray-400 mt-0.5">Review and publish creator hub template submissions.</p>
        </div>

        <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
          {tabs.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === t.key ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'creator' ? <CreatorHubTab /> : <PublishedTab />}
      </div>
    </AdminLayout>
  );
};

export default AdminApprovals;
